import React, { FunctionComponent } from 'react';
import { CompanyDetailHeading } from '../../organisms/companyDetailHeading';
import { CompanyDetailMainContent } from '../../organisms/companyDetailMainContent';
import { CompanyDetailInformation } from '../../organisms/companyDetailInformation';
import { CompanyDetailRecruitments } from '../../organisms/companyDetailRecruitments';
import { CompanyDetailEmployments } from '../../organisms/companyDetailEmployments';
import { CompanyDetailBrands } from '../../organisms/companyDetailBrands';
import { CompanyDetailEvents } from '../../organisms/companyDetailEvents';
import { CompanyDetailStories } from '../../organisms/companyDetailStories';
import { CompanyDetailTvs } from '../../organisms/companyDetailTvs';
import { CompanyDetailAbout } from '../../organisms/companyDetailAbout';
import { CompanyDetailPeople } from '../../organisms/companyDetailPeople';
import classnames from 'classnames';
import type { CompanyDetailProps, CompanyDetailState, RecruitmentPopupVariant } from './types';

const recruitmentPopupContents: Record<RecruitmentPopupVariant, { href: string; label: string }> = {
  popup_to_recruitments: {
    href: '/search',
    label: '最新の求人一覧を見る',
  },
  popup_to_top: {
    href: '/',
    label: 'TOPページから探す',
  },
  popup_to_brands: {
    href: '/brands',
    label: 'ブランド一覧から探す',
  },
  popup_to_companies: {
    href: '/companies',
    label: '会社一覧から探す',
  },
};

type Props = CompanyDetailProps & {
  isPc: boolean;
  state: CompanyDetailState;
  setIdCount: (name: 'recruitmentIds' | 'eventIds' | 'postIds' | 'memberIds' | 'brandIds', value: number) => void;
  employmentRef: React.RefObject<HTMLElement>;
  handleModalClose: () => void;
};

export const Presentation: FunctionComponent<Props> = ({
  company,
  iconImage,
  iconNoImg,
  coverImg,
  coverNoImg,
  noimage,
  hoverIcon,
  cancelIcon,
  user,
  isPage,
  isTitle,
  isDisplayed,
  isShow,
  isStopped,
  isCompanyPage,
  isPc,
  state,
  setIdCount,
  employmentRef,
  handleModalClose,
  lineIcon,
  hasNoReleasedRecruitments,
  recruitmentPopupVariant,
}) => {
  const recruitmentPopupContent = recruitmentPopupVariant
    ? recruitmentPopupContents[recruitmentPopupVariant]
    : undefined;

  return (
    <section className="company__page">
      <CompanyDetailHeading
        company={company}
        iconImage={iconImage}
        iconNoImg={iconNoImg}
        user={user}
        coverImg={coverImg}
        coverNoImg={coverNoImg}
        isPc={isPc}
        setIdCount={setIdCount}
        isPage={isPage}
        isDisplayed={isDisplayed}
        isStopped={isStopped}
        isShow={isShow}
        noimage={noimage}
        hoverIcon={hoverIcon}
        cancelIcon={cancelIcon}
        companyId={company.id}
        dataCategory={state.dataCategory}
        dataValue={state.dataValueCompanyPageHeading}
        isCompanyPage={isCompanyPage}
        lineIcon={lineIcon}
        hasNoReleasedRecruitments={hasNoReleasedRecruitments}
      />

      {isPc && isPage === 'show' && (
        <CompanyDetailMainContent
          coverImg={coverImg}
          coverNoImg={coverNoImg}
        />
      )}

      {isPage === 'show' && (
        <>
          <CompanyDetailInformation
            informationFixedTitle="何をやっているのか/独自のサービス"
            missionFixedTitle="企業の役割（企業理念）"
            company={company}
            isPc={isPc}
          />

          <CompanyDetailRecruitments
            company={company}
            isPc={isPc}
            setIdCount={setIdCount}
            coverNoImg={coverNoImg}
            isStopped={isStopped}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageRecruitments}
          />

          <CompanyDetailEmployments
            company={company}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageEmployments}
            employmentRef={employmentRef}
          />

          <CompanyDetailBrands
            company={company}
            isPc={isPc}
            setIdCount={setIdCount}
            coverNoImg={coverNoImg}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageBrands}
          />

          <div className="company__page__recruitments maskno">
            <div className="fw-slide"></div>
          </div>

          <CompanyDetailEvents
            company={company}
            isPc={isPc}
            setIdCount={setIdCount}
            coverNoImg={coverNoImg}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageEvents}
          />

          <CompanyDetailStories
            company={company}
            isPc={isPc}
            setIdCount={setIdCount}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageStories}
          />

          <CompanyDetailTvs
            company={company}
            isPc={isPc}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageTvs}
          />

          <CompanyDetailAbout
            company={company}
            isPc={isPc}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPageAbout}
          />

          <CompanyDetailPeople
            company={company}
            isPc={isPc}
            setIdCount={setIdCount}
            iconNoImg={iconNoImg}
            dataCategory={state.dataCategory}
            dataValue={state.dataValueCompanyPagePeople}
          />
        </>
      )}
      {recruitmentPopupContent && user === 'without_login' && hasNoReleasedRecruitments && (
        <>
          <div
            className={classnames('company-info-page__overlay', { active: state.isModalOpen })}
            onClick={handleModalClose}
          />
          <div
            className={classnames('company-info-page__modal', 'gtm-popup-view-count', { active: state.isModalOpen })}
            role="dialog"
            aria-modal="true"
            aria-labelledby="company-recruitment-popup-title"
            data-event-name="popup on screen"
            data-category="companycontent_popup"
            data-value="popup"
          >
            <div className="company-info-page__modal__heading">
              <button
                type="button"
                className="company-info-page__modal__close-button"
                onClick={handleModalClose}
                aria-label="閉じる"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">
                  <path
                    d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z"
                    fill="#19190F"
                  />
                </svg>
              </button>
              <p id="company-recruitment-popup-title" className="company-info-page__modal__title">
                最新情報をチェック
              </p>
            </div>
            <div className="company-info-page__modal__main">
              <p className="company-info-page__modal__lead">
                こちらの企業の求人は、<br />現在募集を終了しています。
              </p>
              <p className="company-info-page__modal__description">
                条件に合う求人を<br />最新の一覧ページからチェックしてみませんか？
              </p>
              <a
                href={recruitmentPopupContent.href}
                className="company-info-page__modal__link"
                data-event="click"
                data-category="companycontent"
                data-value={recruitmentPopupVariant}
              >
                {recruitmentPopupContent.label}
              </a>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
